import { type RouteRecordRaw } from 'vue-router';
import type { UserPermission } from '@/stores/usePermissionsStore';
import SSORedirectView from '@/views/SSORedirectView.vue';
import ElectronToastCall from '@/components/call-popups/ElectronToastCall.vue';

export const RouteName = {
  Login: 'Login',
  Phone: 'Phone',
  Contacts: 'Contacts',
  Toast: 'Toast',
  Call: 'Call',
  AdminPortal: {
    Users: {
      AdminUsers: 'AdminUsers',
      AdminUsersDetails: 'AdminUserDetails',
    },
    PhoneBook: {
      CentralContacts: 'CentralContacts',
      CentralGroups: 'CentralGroups',
    },
    TelephonySettings: {
      Blacklist: 'Blacklist',
      RingGroups: 'RingGroups',
      IVR: 'IVR',
      Pickup: 'Pickup',
      TimeConditions: 'TimeConditions',
    },
    CallCenterSettings: {
      QueuesSettings: 'QueuesSettings',
    },
    Integration: {
      AzureSettings: 'AzureSettings',
    },
    AudioLibrary: {
      AudioLibrarySettings: 'AudioLibrarySettings',
    },
    SSO: {
      SSOSettings: 'SSOSettings',
    },
    Provisioning: {
      DeviceManagement: 'DeviceManagement',
      DeviceProfilesManagement: 'DeviceProfilesManagement',
      ProvisioningConfigurationOptions: 'ProvisioningConfigurationOptions',
    },
    Domain: {
      DomainSettings: 'DomainSettings',
    },
  },
  ProfileSettings: 'ProfileSettings',
  CallForwardSettings: 'CallForwardSettings',
  PhonebookSettings: 'PhonebookSettings',
  FollowMeSettings: 'FollowMeSettings',
  ExtensionSettings: 'ExtensionSettings',
  VoicemailSettings: 'VoicemailSettings',
  AudioSettings: 'AudioSettings',
  NotificationSettings: 'NotificationSettings',
  ClientInformationSettings: 'ClientInformationSettings',
  CallScriptSettings: 'CallScriptSettings',
  ContactsSettings: 'ContactsSettings',
  HotkeysSettings: 'HotkeysSettings',
  Pickup: 'Pickup',
  PasswordResetRequest: 'RequestPasswordReset',
  PasswordReset: 'PasswordReset',
  SwitchBoard: 'SwitchBoard',
  Queues: 'Queues',
};

const routes: Readonly<RouteRecordRaw[]> = [
  {
    path: '/toast',
    name: RouteName.Toast,
    component: ElectronToastCall,
    meta: { requiresAuth: false, electronOnly: true },
  },
  {
    path: '/reset_password/',
    name: RouteName.PasswordReset,
    component: () => import('@/views/PasswordReset.vue'),
  },
  {
    path: '/sso-redirect',
    name: 'SSORedirect',
    component: SSORedirectView,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/public',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      {
        path: 'login',
        name: RouteName.Login,
        component: () => import('@/views/LoginView.vue'),
      },
      {
        path: 'requestpasswordreset',
        name: RouteName.PasswordResetRequest,
        component: () => import('@/views/RequestPasswordReset.vue'),
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/AuthenticatedLayout.vue'), // General authenticated layout
    meta: { requiresAuth: true },
    children: [
      {
        path: '/client',
        component: () => import('@/layouts/ClientLayout.vue'),
        children: [
          {
            path: 'phone',
            name: RouteName.Phone,
            component: () => import('@/views/PhoneView.vue'),
          },
          {
            path: 'contacts',
            name: RouteName.Contacts,
            component: () => import('@/views/ContactsView.vue'),
          },
          {
            path: 'call/:callid',
            name: RouteName.Call,
            component: () => import('@/views/Call.vue'),
          },
          {
            path: 'queues',
            name: RouteName.Queues,
            meta: { requiredPermissions: ['agent', 'supervisor'] as UserPermission[] },
            component: () => import('@/views/CallCenterView.vue'),
          },
          {
            path: 'switchboard',
            name: RouteName.SwitchBoard,
            meta: { requiredPermissions: ['switchboard'] as UserPermission[] },
            component: () => import('@/views/SwitchBoardView.vue'),
          },
        ],
      },
      {
        path: '/admin-portal/main',
        name: 'Overview',
        meta: { requiredPermissions: ['admin', 'superadmin', 'customer-admin'] as UserPermission[] },
        component: () => import('@/views/AdminPortalLayout.vue'),
        children: [
          {
            path: 'phb/cc',
            meta: { adminPortalCategory: 'phonebook-settings' },
            name: RouteName.AdminPortal.PhoneBook.CentralContacts,
            component: () => import('@/views/CentralContactsView.vue'),
          },
          {
            path: 'phb/cg',
            meta: { adminPortalCategory: 'phonebook-settings' },
            name: RouteName.AdminPortal.PhoneBook.CentralGroups,
            component: () => import('@/views/CentralGroupsView.vue'),
          },
          {
            path: 'cs/bl',
            meta: { adminPortalCategory: 'call-settings' },
            name: RouteName.AdminPortal.TelephonySettings.Blacklist,
            component: () => import('@/views/BlacklistView.vue'),
          },
          {
            path: 'cs/ccg',
            meta: { adminPortalCategory: 'call-settings' },
            name: RouteName.AdminPortal.TelephonySettings.RingGroups,
            component: () => import('@/views/RingGroupsView.vue'),
          },
          {
            path: 'cs/ivr',
            meta: { adminPortalCategory: 'call-settings' },
            name: RouteName.AdminPortal.TelephonySettings.IVR,
            component: () => import('@/views/IVRView.vue'),
          },
          {
            path: 'cs/pu',
            meta: { adminPortalCategory: 'call-settings' },
            name: RouteName.AdminPortal.TelephonySettings.Pickup,
            component: () => import('@/views/PickupView.vue'),
          },
          {
            path: 'cs/tc',
            meta: { adminPortalCategory: 'call-settings' },
            name: RouteName.AdminPortal.TelephonySettings.TimeConditions,
            component: () => import('@/views/TimeConditionsView.vue'),
          },
          {
            path: 'cc/q',
            meta: { adminPortalCategory: 'callcenter-settings' },
            name: RouteName.AdminPortal.CallCenterSettings.QueuesSettings,
            component: () => import('@/views/CallCenterQueuesView.vue'),
          },
          {
            path: 'in/as',
            meta: { adminPortalCategory: 'integration-settings' },
            name: RouteName.AdminPortal.Integration.AzureSettings,
            component: () => import('@/views/AzureIntegrationView.vue'),
          },
          {
            path: 'ml/ad',
            meta: { adminPortalCategory: 'medialibrary-settings' },
            name: RouteName.AdminPortal.AudioLibrary.AudioLibrarySettings,
            component: () => import('@/views/AudioLibraryView.vue'),
          },
          {
            path: 'sso/sso',
            meta: { adminPortalCategory: 'sso-settings' },
            name: RouteName.AdminPortal.SSO.SSOSettings,
            component: () => import('@/views/SSOSettingsView.vue'),
          },
          {
            path: 'prv/dm',
            meta: { adminPortalCategory: 'provision-settings' },
            name: RouteName.AdminPortal.Provisioning.DeviceManagement,
            component: () => import('@/views/DeviceManagementView.vue'),
          },
          {
            path: 'prv/dpm',
            meta: { adminPortalCategory: 'provision-settings' },
            name: RouteName.AdminPortal.Provisioning.DeviceProfilesManagement,
            component: () => import('@/views/DeviceProfilesManagementView.vue'),
          },
          {
            path: 'prv/pco',
            meta: { adminPortalCategory: 'provision-settings' },
            name: RouteName.AdminPortal.Provisioning.ProvisioningConfigurationOptions,
            component: () => import('@/views/ProvisioningConfigurationOptionsView.vue'),
          },
          {
            path: 'do/ds',
            meta: { adminPortalCategory: 'domain-settings' },
            name: RouteName.AdminPortal.Domain.DomainSettings,
            component: () => import('@/views/DomainSettingsView.vue'),
          },
        ],
      },
      {
        path: '/admin-portal/users',
        name: RouteName.AdminPortal.Users.AdminUsers,
        component: () => import('@/views/UserAdministrationView.vue'),
        meta: { requiredPermissions: ['admin', 'superadmin', 'customer-admin'] as UserPermission[] },
        children: [
          {
            name: RouteName.AdminPortal.Users.AdminUsersDetails,
            path: ':uuid',
            component: () => import('@/components/admin-portal/users/admin-user-detail-form/AdminUserDetailForm.vue'),
          },
        ],
      },
      {
        path: '/settings',
        name: 'Settings',
        component: () => import('@/views/SettingsLayout.vue'),
        children: [
          {
            path: 'profile',
            name: RouteName.ProfileSettings,
            component: () => import('@/views/settings/ProfileSettingsView.vue'),
          },
          {
            path: 'call-forward',
            name: RouteName.CallForwardSettings,
            component: () => import('@/modules/settings/call-forward/CallForwardSettingsView.vue'),
          },
          {
            path: 'follow-me',
            name: RouteName.FollowMeSettings,
            component: () => import('@/modules/settings/follow-me/FollowMeSettingsView.vue'),
          },
          {
            path: 'extension',
            name: RouteName.ExtensionSettings,
            component: () => import('@/modules/settings/extension-settings/ExtensionSettingsView.vue'),
          },
          {
            path: 'voicemail',
            name: RouteName.VoicemailSettings,
            component: () => import('@/views/settings/VoicemailSettingsView.vue'),
          },
          {
            path: 'audio',
            name: RouteName.AudioSettings,
            component: () => import('@/views/settings/AudioSettingsView.vue'),
          },
          {
            path: 'notification',
            name: RouteName.NotificationSettings,
            component: () => import('@/views/settings/NotificationSettingsView.vue'),
          },
          {
            path: 'client-information',
            name: RouteName.ClientInformationSettings,
            component: () => import('@/views/settings/ClientInformationView.vue'),
          },
          {
            path: 'call-scripts',
            name: RouteName.CallScriptSettings,
            component: () => import('@/views/settings/CallScriptView.vue'),
          },
          {
            path: 'contacts',
            name: RouteName.ContactsSettings,
            component: () => import('@/views/settings/ContactsSettingsView.vue'),
          },
          {
            path: 'hotkeys',
            name: RouteName.HotkeysSettings,
            component: () => import('@/views/settings/HotkeysView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/client/phone',
  },
];

export default routes;
